import { gql } from "generated-graphql";

/**
 * Everything the notification preferences page renders, in one request per tab.
 *
 * The whole page — the delivery switches, the sections, the rows and their
 * per-channel choices — comes from this single query. `scope` chooses the layer
 * the values are resolved at, so the "My preferences" and "Organization
 * defaults" tabs are the same document with a different variable. Apollo caches
 * them separately by variables, which is what lets a tab keep its own answer
 * without either tab having to re-derive the other's.
 *
 * `audience` picks the sections: an organization's own, or a staff member's
 * Encamp-only alerts, which are the only catalog that may omit `tenantId` to mean
 * every organization.
 *
 * `ownEnabled` is the toggle position and is what every switch renders. `enabled`
 * and `silencedByParent` are the server's view of what would actually be
 * delivered — they diverge from `ownEnabled` exactly while a parent is muting the
 * row — and are selected as the answer to compare against rather than as the source
 * the page draws from. The page must not explain a mute from them: they describe the
 * state at load time and are stale from the first switch the reader flips, so
 * silencing is recomputed from the draft instead.
 */
export const MY_NOTIFICATION_PREFERENCE_CATALOG = gql(`
  query MyNotificationPreferenceCatalog(
    $tenantId: ID
    $scope: NotificationPreferenceScope!
    $audience: NotificationPreferenceAudience!
  ) {
    myNotificationPreferenceCatalog(tenantId: $tenantId, scope: $scope, audience: $audience) {
      channelParentSwitches {
        kind
        channel
        enabled
        source
      }
      emailDigest {
        source
        timeZone
        sendHourLocal
      }
      sections {
        groupId
        label
        staffOnly
        parentSwitches {
          kind
          channel
          enabled
          source
        }
        rows {
          kind
          title
          description
          linksTo
          when {
            control
            fixedLabel
            leadTimeDays
            thresholdPercent
          }
          preferences {
            kind
            channel
            enabled
            ownEnabled
            silencedByParent
            defaultEnabled
            digestCadence
            source
          }
          pausedForAllStaff
        }
      }
    }
  }
`);

/**
 * Stores a batch of preference overrides. The page sends only the entries whose
 * toggle actually moved: an untouched row must stay absent so it keeps following
 * whichever layer it was following, rather than being frozen at today's resolved
 * value.
 */
export const SET_NOTIFICATION_PREFERENCES = gql(`
  mutation SetNotificationPreferences($tenantId: ID, $input: [SetNotificationPreferenceInput!]!) {
    setNotificationPreferences(tenantId: $tenantId, input: $input)
  }
`);

/**
 * Deletes the caller's own override rows so they follow the organization again.
 * Deliberately not a batch of writes: an absent row means "follow the
 * organization", and copying today's organization values onto the caller would
 * look identical the moment it ran and then freeze them out of every later
 * change to those defaults.
 */
export const RESET_MY_NOTIFICATION_PREFERENCES = gql(`
  mutation ResetMyNotificationPreferences(
    $tenantId: ID
    $audience: NotificationPreferenceAudience!
  ) {
    resetMyNotificationPreferences(tenantId: $tenantId, audience: $audience)
  }
`);

/**
 * The Standard preset, which is a *delete* rather than a write.
 *
 * Encamp's recommended posture is the registry default, which is exactly what an
 * organization with no rows of its own already resolves to — so writing those
 * values would change nothing on the day it ran and would then freeze this
 * organization out of every future change Encamp makes to its recommendations.
 * Deleting achieves the same posture and keeps the organization following it.
 */
export const RESET_ORGANIZATION_NOTIFICATION_PREFERENCES = gql(`
  mutation ResetOrganizationNotificationPreferences($tenantId: ID!) {
    resetOrganizationNotificationPreferences(tenantId: $tenantId)
  }
`);

/**
 * Whether the alerts this save is about to switch on have conditions already open
 * and waiting.
 *
 * Asked on the Save click rather than kept live with the draft. The answer only
 * matters at the moment of the write, the question needs the final set of kinds to
 * be meaningful, and a query that followed every switch would ask it dozens of times
 * per visit to warn about one.
 */
export const HAS_OPEN_NOTIFICATIONS_TO_DELIVER = gql(`
  query HasOpenNotificationsToDeliver(
    $tenantId: ID
    $kinds: [String!]!
    $scope: NotificationPreferenceScope!
  ) {
    hasOpenNotificationsToDeliver(tenantId: $tenantId, kinds: $kinds, scope: $scope)
  }
`);

/**
 * The organizations a staff member follows, for the Encamp alerts tab's list:
 * their Salesforce account team plus the ones they added, minus the ones they removed.
 */
export const MY_SUBSCRIBED_ORGANIZATIONS = gql(`
  query MySubscribedOrganizations {
    mySubscribedOrganizations {
      tenantId
      name
      accountRoles
      hasOverrides
    }
  }
`);

/** Follows or stops following an organization; answers with the whole updated list. */
export const SET_TENANT_NOTIFICATION_SUBSCRIPTION = gql(`
  mutation SetTenantNotificationSubscription($tenantId: ID!, $subscribed: Boolean!) {
    setTenantNotificationSubscription(tenantId: $tenantId, subscribed: $subscribed) {
      tenantId
      name
      accountRoles
      hasOverrides
    }
  }
`);

/** Pauses alerts for every staff member, or resumes them. */
export const SET_STAFF_NOTIFICATION_PAUSES = gql(`
  mutation SetStaffNotificationPauses($kinds: [String!]!, $paused: Boolean!) {
    setStaffNotificationPauses(kinds: $kinds, paused: $paused)
  }
`);
